import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from './api/client';
import ShipmentsToolbar from './components/ShipmentsToolbar';
import WalmartShipmentsTable from './components/WalmartShipmentsTable';
import WalmartStockUploadPopup from './yoy-components/WalmartStockUploadPopup';
import { useWalmartStockUpload } from './yoy-components/useWalmartStockUpload';
import { useWalmartMonthlyForecast } from './components/useWalmartMonthlyForecast';
import ConfirmPopup from './components/ConfirmPopup';

const STORAGE_KEY = 'sp_wm_view_settings';

const getDateNDaysAhead = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const WalmartShipmentsTab = () => {
  const [products, setProducts]     = useState([]);
  const [brands, setBrands]         = useState([]);
  const [niches, setNiches]         = useState([]);
  const [dailyPlan, setDailyPlan]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [boxInputs, setBoxInputs]   = useState({});
  const [boxSaveStatus, setBoxSaveStatus] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [velocityData, setVelocityData]       = useState({});
  const [actualSalesData, setActualSalesData] = useState({});
  const [producedData, setProducedData]       = useState({});
  const [commentsData, setCommentsData]       = useState({});
  const [wfsUpdatedAt, setWfsUpdatedAt]       = useState(null);

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  };
  const savedSettings = loadSettings();

  const [searchQuery, setSearchQuery]       = useState(savedSettings.searchQuery || '');
  const [selectedBrands, setSelectedBrands] = useState(savedSettings.selectedBrands || []);
  const [selectedNiches, setSelectedNiches] = useState(savedSettings.selectedNiches || []);
  const [selectedTypes, setSelectedTypes]   = useState(savedSettings.selectedTypes || []);
  const _savedShipDate = savedSettings.shipDate;
  const _today = getDateNDaysAhead(0);
  const [shipDate, setShipDate] = useState(
    _savedShipDate && _savedShipDate >= _today ? _savedShipDate : getDateNDaysAhead(3)
  );
  const _savedBoxes = savedSettings.minBoxesFilter;
  const [minBoxesFilter, setMinBoxesFilter] = useState(
    _savedBoxes && typeof _savedBoxes === 'object' ? _savedBoxes : { min: '', max: '' }
  );
  const [showShipmentOnly, setShowShipmentOnly] = useState(false);
  const [showOosOnly, setShowOosOnly] = useState(false);
  const [sizeFilter, setSizeFilter] = useState(savedSettings.sizeFilter || '');

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        searchQuery, selectedBrands, selectedNiches, selectedTypes, shipDate, minBoxesFilter, sizeFilter,
      }));
    } catch {}
  }, [searchQuery, selectedBrands, selectedNiches, selectedTypes, shipDate, minBoxesFilter, sizeFilter]);

  const reloadProducts = useCallback(async () => {
    try {
      const { data } = await api.get(`/shipments-planning/walmart-products`);
      setProducts(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const fetchTimestamps = useCallback(async () => {
    try {
      const { data: { wfs_updated_at } } = await api.get(`/shipments-planning/walmart-stock-timestamps`);
      setWfsUpdatedAt(wfs_updated_at || null);
    } catch {}
  }, []);

  const fetchVelocity = useCallback(async () => {
    try {
      const { data: rows } = await api.get(`/shipments-planning/walmart-velocity`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row; });
      setVelocityData(map);
    } catch {}
  }, []);

  const fetchActualSales = useCallback(async () => {
    try {
      const { data: rows } = await api.get(`/shipments-planning/walmart-actual-sales`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row; });
      setActualSalesData(map);
    } catch {}
  }, []);

  const fetchBoxInputs = useCallback(async () => {
    try {
      const { data: rows } = await api.get(`/shipments-planning/walmart-box-inputs`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row.boxes; });
      setBoxInputs(map);
    } catch {}
  }, []);

  const fetchComments = useCallback(async () => {
    try {
      const { data: rows } = await api.get(`/shipments-planning/comments`);
      const map = {};
      rows.forEach(row => { map[row.sku] = row.comment; });
      setCommentsData(map);
    } catch {}
  }, []);

  const saveComment = useCallback(async (sku, comment) => {
    setCommentsData(prev => ({ ...prev, [sku]: comment }));
    try {
      await api.post(`/shipments-planning/comments/save`, { sku, comment });
    } catch {}
  }, []);

  const saveBoxInputs = useCallback(async () => {
    setBoxSaveStatus('saving');
    try {
      const inputs = {};
      Object.entries(boxInputs).forEach(([sku, val]) => {
        if (val !== '' && Number(val) > 0) inputs[sku] = Number(val);
      });
      await api.post(`/shipments-planning/walmart-box-inputs/save`, { inputs });
      setBoxSaveStatus('saved');
      setTimeout(() => setBoxSaveStatus(null), 2500);
    } catch {
      setBoxSaveStatus('error');
      setTimeout(() => setBoxSaveStatus(null), 3000);
    }
  }, [boxInputs]);

  const confirmClearBoxInputs = useCallback(async () => {
    setShowClearConfirm(false);
    try {
      await api.post(`/shipments-planning/walmart-box-inputs/clear`);
      setBoxInputs({});
    } catch {}
  }, []);

  // WFS stock upload — the same popup & table as YoY Forecast (walmart_stock)
  const stockUpload = useWalmartStockUpload(() => {
    fetchTimestamps();
    reloadProducts();
  });

  // Forecast from YoY monthly data (Walmart tab, keyed by SKU)
  const { forecastSalesData } = useWalmartMonthlyForecast(products);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [prodRes, brandRes, nicheRes] = await Promise.all([
          api.get(`/shipments-planning/walmart-products`),
          api.get(`/shipments-planning/brands`),
          api.get(`/shipments-planning/niches`),
        ]);
        const [prodData, brandData, nicheData] = [prodRes.data, brandRes.data, nicheRes.data];
        setProducts(Array.isArray(prodData) ? prodData : []);
        setBrands(Array.isArray(brandData) ? brandData : []);
        setNiches(Array.isArray(nicheData) ? nicheData : []);
      } catch (err) {
        console.error('Error loading walmart shipments data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    fetchTimestamps();
    fetchVelocity();
    fetchActualSales();
    fetchBoxInputs();
    fetchComments();
  }, [fetchTimestamps, fetchVelocity, fetchActualSales, fetchBoxInputs, fetchComments]);

  useEffect(() => {
    const [y, m, d] = shipDate.split('-').map(Number);
    const ship = new Date(y, m - 1, d);
    const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    const fromDate = new Date(ship); fromDate.setDate(fromDate.getDate() - 5);
    const toDate   = new Date(ship); toDate.setDate(toDate.getDate() - 1);
    const from = fmt(fromDate);
    const to   = fmt(toDate);

    Promise.all([
      api.get(`/shipments-planning/daily-plan?from=${from}&to=${to}`).then(r => r.data),
      api.get(`/shipments-planning/produced?from=${from}&to=${to}`).then(r => r.data),
    ]).then(([planData, prodData]) => {
      setDailyPlan(Array.isArray(planData) ? planData : []);
      const map = {};
      if (Array.isArray(prodData)) {
        prodData.forEach(row => {
          if (!map[row.sku]) map[row.sku] = {};
          map[row.sku][row.date] = (map[row.sku][row.date] || 0) + Number(row.produced);
        });
      }
      setProducedData(map);
    }).catch(err => console.error('Error loading daily plan / produced:', err));
  }, [shipDate]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q ||
        p.name?.toLowerCase().includes(q) ||
        p.SKU?.toLowerCase().includes(q) ||
        p.GTIN?.toLowerCase().includes(q);
      const matchBrand = selectedBrands.length === 0 || selectedBrands.includes(String(p.brandID));
      const matchNiche = selectedNiches.length === 0 || selectedNiches.includes(String(p.niche_category_id));
      const matchType  = selectedTypes.length === 0  || selectedTypes.includes(p.type);
      const matchShipment = !showShipmentOnly ||
        (boxInputs[p.SKU] !== undefined && boxInputs[p.SKU] !== '' && Number(boxInputs[p.SKU]) > 0);
      const matchSize = sizeFilter === '' ||
        (p.pack_size_oz != null && Number(p.pack_size_oz) === Number(sizeFilter));
      const matchOos = !showOosOnly || (p.GTIN && Number(p.stock_wfs_available) === 0);
      return matchSearch && matchBrand && matchNiche && matchType && matchShipment && matchSize && matchOos;
    });
  }, [products, searchQuery, selectedBrands, selectedNiches, selectedTypes, showShipmentOnly, boxInputs, sizeFilter, showOosOnly]);

  const packSizes = useMemo(() => {
    const set = new Set();
    products.forEach(p => { if (p.pack_size_oz != null) set.add(Number(p.pack_size_oz)); });
    return Array.from(set).sort((a, b) => a - b);
  }, [products]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedBrands([]);
    setSelectedNiches([]);
    setSelectedTypes([]);
    setMinBoxesFilter({ min: '', max: '' });
    setShowShipmentOnly(false);
    setShowOosOnly(false);
    setSizeFilter('');
  }, []);

  const isBoxesFilterActive = minBoxesFilter.min !== '' || minBoxesFilter.max !== '';
  const hasActiveFilters = searchQuery || selectedBrands.length > 0 || selectedNiches.length > 0 || selectedTypes.length > 0 || isBoxesFilterActive || showShipmentOnly || showOosOnly || sizeFilter !== '';

  // Velocity и Actual Sales с 31.08.2026 считаются сервером из walmart_week_sales
  // (автосинк Walmart API) — файловая загрузка продаж удалена.

  return (
    <>
      <ShipmentsToolbar
        searchQuery={searchQuery}       setSearchQuery={setSearchQuery}
        selectedBrands={selectedBrands} setSelectedBrands={setSelectedBrands}
        selectedNiches={selectedNiches} setSelectedNiches={setSelectedNiches}
        selectedTypes={selectedTypes}   setSelectedTypes={setSelectedTypes}
        brands={brands}
        niches={niches}
        filteredCount={filteredProducts.length}
        totalCount={products.length}
        hasActiveFilters={hasActiveFilters}
        clearFilters={clearFilters}
        shipDate={shipDate}
        setShipDate={setShipDate}
        onOpenFbaPopup={() => stockUpload.setShowWalmartStockPopup(true)}
        stockButtonLabel="WFS Stock"
        showAwd={false}
        showExport={false}
        showSkuMapping={false}
        fbaUpdatedAt={wfsUpdatedAt}
        minBoxesFilter={minBoxesFilter}
        setMinBoxesFilter={setMinBoxesFilter}
        showShipmentOnly={showShipmentOnly}
        setShowShipmentOnly={setShowShipmentOnly}
        showOosOnly={showOosOnly}
        setShowOosOnly={setShowOosOnly}
        sizeFilter={sizeFilter}
        setSizeFilter={setSizeFilter}
        packSizes={packSizes}
      />

      {loading ? (
        <div className="sp-loading">
          <i className="fas fa-circle-notch sp-spin"></i>
          <span>Loading products…</span>
        </div>
      ) : (
        <WalmartShipmentsTable
          products={filteredProducts}
          dailyPlan={dailyPlan}
          producedData={producedData}
          shipDate={shipDate}
          boxInputs={boxInputs}
          setBoxInputs={setBoxInputs}
          wfsUpdatedAt={wfsUpdatedAt}
          velocityData={velocityData}
          actualSalesData={actualSalesData}
          forecastSalesData={forecastSalesData}
          minBoxesFilter={minBoxesFilter}
          onSaveBoxInputs={saveBoxInputs}
          onClearBoxInputs={() => setShowClearConfirm(true)}
          boxSaveStatus={boxSaveStatus}
          commentsData={commentsData}
          onSaveComment={saveComment}
        />
      )}

      {/* WFS stock upload — shared popup with YoY Forecast (walmart_stock table) */}
      <WalmartStockUploadPopup
        showWalmartStockPopup={stockUpload.showWalmartStockPopup}
        setShowWalmartStockPopup={stockUpload.setShowWalmartStockPopup}
        selectedFile={stockUpload.selectedFile}
        setSelectedFile={stockUpload.setSelectedFile}
        parsedData={stockUpload.parsedData}
        setParsedData={stockUpload.setParsedData}
        uploadStatus={stockUpload.uploadStatus}
        setUploadStatus={stockUpload.setUploadStatus}
        fileInputRef={stockUpload.fileInputRef}
        handleFileUpload={stockUpload.handleFileUpload}
        handleUploadSubmit={stockUpload.handleUploadSubmit}
        closePopup={stockUpload.closePopup}
      />

      {/* Clear boxes confirmation */}
      {showClearConfirm && (
        <ConfirmPopup
          title="Clear all box quantities?"
          message="All planned Walmart box quantities will be removed from the database. This cannot be undone."
          confirmLabel="Yes, clear all"
          cancelLabel="Cancel"
          danger
          onConfirm={confirmClearBoxInputs}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}
    </>
  );
};

export default WalmartShipmentsTab;
